import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Modal, ModalBody } from 'reactstrap';
import { Check, X, BookOpen } from 'react-feather';
import { useAuth } from 'Utils/Hooks/useAuth';
import { Button, Loading, Meter } from 'Shared/UI';
import useAudience, { helpBase } from './useAudience';
import useChecklist from './useChecklist';
import './help.scss';

const VERSION = 'v1';
const EVENT = 'ed:welcome-guide';
const keyFor = (user) => `ed-welcome-${VERSION}-${user?.id || user?.email || 'anon'}`;

const seen = (user) => {
  try {
    return window.localStorage.getItem(keyFor(user)) === '1';
  } catch (e) {
    return false;
  }
};
const markSeen = (user) => {
  try {
    window.localStorage.setItem(keyFor(user), '1');
  } catch (e) {
    /* storage unavailable: the guide simply shows again next time */
  }
};

/** Opens the guide from anywhere (help menu, help centre). */
export const openWelcomeGuide = () => window.dispatchEvent(new Event(EVENT));

const INTRO = {
  admin: {
    title: 'Welcome to the Eudor Innovations operations portal',
    text: 'Everything partners and networks need from us is here. This is the order most days go in.',
  },
  pending: {
    title: 'Welcome to Eudor Innovations',
    text: 'One connection to MTN, Airtel, Glo and 9mobile. First we need to verify your company; meanwhile you can start building in the sandbox.',
  },
  partner: {
    title: 'Welcome to Eudor Innovations',
    text: 'Here’s how to take a service live on the networks. Ticks appear as you complete each step.',
  },
};

/** The step list, shared by the login dialog and the overview card. */
export const StepList = ({ steps, onNavigate, audience }) => (
  <ol className="wg-steps">
    {steps.map((s, i) => (
      <li key={s.key} className={s.done ? 'is-done' : ''}>
        <span className="wg-steps__mark" aria-hidden="true">
          {s.done ? <Check size={14} strokeWidth={3} /> : i + 1}
        </span>
        <div className="wg-steps__body">
          <span className="wg-steps__title">
            {s.title}
            {s.count > 0 && <span className="wg-steps__count">{s.count}</span>}
            {s.done && <span className="ed-visually-hidden"> (done)</span>}
          </span>
          <span className="wg-steps__text">{s.text}</span>
        </div>
        <div className="wg-steps__actions">
          <Link to={s.to} onClick={onNavigate} className={`ed-btn ed-btn--sm ${s.done ? 'ed-btn--ghost' : 'ed-btn--secondary'}`}>
            {s.done ? 'Open' : 'Start'}
          </Link>
          <Link to={`${helpBase(audience)}/${s.help}`} onClick={onNavigate} className="wg-steps__help" aria-label={`How to: ${s.title}`}>
            <BookOpen size={15} aria-hidden="true" />
          </Link>
        </div>
      </li>
    ))}
  </ol>
);

/**
 * Shown once after a user's first sign-in (per user, per browser) and whenever opened from
 * the help menu.
 */
const WelcomeGuide = () => {
  const { currentAdmin } = useAuth();
  const audience = useAudience();
  const [open, setOpen] = useState(false);
  const { steps, loading } = useChecklist(audience, open);

  useEffect(() => {
    if (currentAdmin && !seen(currentAdmin)) setOpen(true);
  }, [currentAdmin]);
  useEffect(() => {
    const show = () => setOpen(true);
    window.addEventListener(EVENT, show);
    return () => window.removeEventListener(EVENT, show);
  }, []);

  const close = () => {
    markSeen(currentAdmin);
    setOpen(false);
  };
  const intro = INTRO[audience];
  const tracked = steps.filter((s) => s.done !== undefined);
  const done = tracked.filter((s) => s.done).length;

  return (
    <Modal isOpen={open} toggle={close} centered size="lg" className="ed-modal wg" labelledBy="wg-title">
      <div className="wg-head">
        <div>
          <h2 id="wg-title" className="wg-title">
            {intro.title}
          </h2>
          <p className="wg-text">{intro.text}</p>
        </div>
        <button type="button" className="ed-icon-btn wg-close" aria-label="Close" onClick={close}>
          <X size={20} />
        </button>
      </div>
      <ModalBody>
        {audience === 'partner' && tracked.length > 0 && !loading && (
          <div className="wg-progress">
            <span>
              {done} of {tracked.length} done
            </span>
            <Meter value={done} total={tracked.length} />
          </div>
        )}
        {loading ? <Loading lines={4} /> : <StepList steps={steps} onNavigate={close} audience={audience} />}
        <div className="wg-foot">
          <Link to={helpBase(audience)} onClick={close} className="ed-btn ed-btn--ghost">
            <BookOpen size={16} aria-hidden="true" />
            <span>Open the help centre</span>
          </Link>
          <Button onClick={close}>Got it</Button>
        </div>
        <p className="wg-later">You can open this guide again from the question mark at the top of every page.</p>
      </ModalBody>
    </Modal>
  );
};

export default WelcomeGuide;
